import React from "react";

import { isEmbedded } from "../features/auth/lucimixHandshake";

export const INVESTMENT_AGENT_PANEL_WIDTH_KEY = "investment_agent_panel_width";
const AGENT_PANEL_WIDTH_EVENT = "astock-agent-panel-width-change";
const AGENT_PANEL_MIN_WIDTH = 300;

function storedWidth(storageKey, fallback) {
  if (!storageKey || typeof window === "undefined") return fallback;
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored === null) return fallback;
    const value = Number(stored);
    return Number.isFinite(value) ? Math.min(640, Math.max(300, value)) : fallback;
  } catch {
    return fallback;
  }
}

// Agent 对话面板的布局交互：拖拽调宽、折叠/展开、折叠后悬浮球拖动。
// 纯 UI 状态，与业务数据无关；isDashboard 影响拖宽时给中间栏保留的最小宽度。
export function useAgentPanelLayout({
  isDashboard,
  autoCollapseAt = null,
  initialWidth = 420,
  centerMinWidth = null,
  rightRailWidth = null,
  storageKey = null,
}) {
  // 嵌在 Lucimix iframe 里时，左侧栏已占掉一截宽度，默认把对话栏收到最窄，把空间让给数据表；
  // 只是「默认值」——用户拖过就以存下的宽度为准，正常打开仍是 initialWidth。
  const [defaultWidth] = React.useState(() => (isEmbedded() ? AGENT_PANEL_MIN_WIDTH : initialWidth));
  const [chatCollapsed, setChatCollapsed] = React.useState(false);
  const [preferredAgentPanelWidth, setPreferredAgentPanelWidth] = React.useState(
    () => storedWidth(storageKey, defaultWidth),
  );
  const [viewportWidth, setViewportWidth] = React.useState(
    () => (typeof window === "undefined" ? 1920 : window.innerWidth),
  );
  const [floatingAgentPosition, setFloatingAgentPosition] = React.useState({ x: 18, y: 88 });
  const ignoreNextFloatingClick = React.useRef(false);
  const compactLayoutRef = React.useRef(false);
  // 区分“窄屏自动折叠”和“用户手动折叠”：切换到不启用自动折叠的模式（如组合驾驶舱）
  // 时只回弹前者——否则单标的首页在 1280~1540 宽下自动折叠后，折叠态会泄漏到
  // 组合模式，三栏塌成两栏（dashboard-smoke E2E 的 LAYOUT-010 断言逮到过）。
  const autoCollapsedRef = React.useRef(false);
  const resolvedCenterMinWidth = centerMinWidth ?? (isDashboard ? 900 : 860);
  const resolvedRightRailWidth = rightRailWidth ?? (isDashboard ? 315 : 340);
  const shellGapAndPadding = 64;
  const maxByViewport = viewportWidth
    - resolvedCenterMinWidth
    - resolvedRightRailWidth
    - shellGapAndPadding;
  const maxWidth = Math.max(340, Math.min(640, maxByViewport));
  const agentPanelWidth = Math.min(maxWidth, Math.max(300, preferredAgentPanelWidth));

  React.useEffect(() => {
    const syncViewport = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  React.useEffect(() => {
    if (!storageKey) return undefined;
    const syncSharedWidth = (event) => {
      if (event.detail?.storageKey !== storageKey) return;
      const nextWidth = Number(event.detail.width);
      if (Number.isFinite(nextWidth)) setPreferredAgentPanelWidth(nextWidth);
    };
    const syncStoredWidth = (event) => {
      if (event.key !== storageKey) return;
      setPreferredAgentPanelWidth(storedWidth(storageKey, defaultWidth));
    };
    window.addEventListener(AGENT_PANEL_WIDTH_EVENT, syncSharedWidth);
    window.addEventListener("storage", syncStoredWidth);
    return () => {
      window.removeEventListener(AGENT_PANEL_WIDTH_EVENT, syncSharedWidth);
      window.removeEventListener("storage", syncStoredWidth);
    };
  }, [defaultWidth, storageKey]);

  function updateAgentPanelWidth(width) {
    setPreferredAgentPanelWidth(width);
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, String(Math.round(width)));
    } catch {
      // localStorage 不可用时仍保留当前页面内的拖动结果。
    }
    window.dispatchEvent(new CustomEvent(AGENT_PANEL_WIDTH_EVENT, {
      detail: { storageKey, width },
    }));
  }

  React.useEffect(() => {
    if (!autoCollapseAt) {
      if (autoCollapsedRef.current) {
        autoCollapsedRef.current = false;
        setChatCollapsed(false);
      }
      return undefined;
    }

    const syncCompactLayout = () => {
      const isCompact = window.innerWidth <= autoCollapseAt;
      if (isCompact && !compactLayoutRef.current) {
        autoCollapsedRef.current = true;
        setChatCollapsed(true);
      }
      compactLayoutRef.current = isCompact;
    };

    syncCompactLayout();
    window.addEventListener("resize", syncCompactLayout);
    return () => window.removeEventListener("resize", syncCompactLayout);
  }, [autoCollapseAt]);

  function startAgentPanelResize(event) {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = agentPanelWidth;
    document.body.classList.add("is-resizing-agent");
    const handleMouseMove = (moveEvent) => {
      const nextWidth = startWidth + moveEvent.clientX - startX;
      updateAgentPanelWidth(Math.min(maxWidth, Math.max(300, nextWidth)));
    };
    const stopResize = () => {
      document.body.classList.remove("is-resizing-agent");
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResize);
      window.removeEventListener("mouseleave", stopResize);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResize);
    window.addEventListener("mouseleave", stopResize);
  }

  function startFloatingAgentDrag(event) {
    const startX = event.clientX;
    const startY = event.clientY;
    const startPosition = floatingAgentPosition;
    let moved = false;
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      moved = moved || Math.abs(deltaX) + Math.abs(deltaY) > 4;
      const maxX = window.innerWidth - 180;
      const maxY = window.innerHeight - 58;
      setFloatingAgentPosition({
        x: Math.min(Math.max(12, startPosition.x + deltaX), Math.max(12, maxX)),
        y: Math.min(Math.max(76, startPosition.y + deltaY), Math.max(76, maxY)),
      });
    };
    const stopDrag = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDrag);
      if (moved) {
        ignoreNextFloatingClick.current = true;
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDrag);
  }

  function toggleChatPanel() {
    if (ignoreNextFloatingClick.current) {
      ignoreNextFloatingClick.current = false;
      return;
    }
    // 用户手动操作后，折叠态归用户所有，切模式不再自动回弹
    autoCollapsedRef.current = false;
    setChatCollapsed((current) => !current);
  }

  return {
    chatCollapsed,
    setChatCollapsed,
    agentPanelWidth,
    floatingAgentPosition,
    startAgentPanelResize,
    startFloatingAgentDrag,
    toggleChatPanel,
  };
}
